import React from "react";
import InputField from "../InputField";
import Dropdown from "../Dropdown";

/** The choice that opens the fields for an address the customer does not have yet. */
export const NEW_ADDRESS_CHOICE = "Add a new address";

/**
 * @param {object} address - { streetAddress, postalCode, city }
 * @returns {string} "Storgatan 1, 262 31 Ängelholm"
 */
export const addressLabel = (address) =>
  `${address.streetAddress}, ${address.postalCode} ${address.city}`;

/**
 * #358: the new user's postal address, independent of Office. A saved address is picked as it
 * is — never edited here, so a typo cannot change the address everyone else already uses — and
 * a new address needs street address, postal code and city.
 *
 * @param {object} props
 * @param {object[]} props.addresses - The customer's saved addresses
 * @param {object} props.user - The form state: addressChoice, streetAddress, postalCode, city
 * @param {(changes: object) => void} props.onChange - Called with the fields to merge into the user
 */
const AddressField = ({ addresses = [], user, onChange }) => {
  const hasAddresses = addresses.length > 0;
  const isNewAddress = !hasAddresses || user.addressChoice === NEW_ADDRESS_CHOICE;

  const handleChoice = (choice) => {
    const chosen = addresses.find((address) => addressLabel(address) === choice);

    onChange(
      chosen
        ? {
            addressChoice: choice,
            streetAddress: chosen.streetAddress,
            postalCode: chosen.postalCode,
            city: chosen.city,
          }
        : { addressChoice: NEW_ADDRESS_CHOICE, streetAddress: "", postalCode: "", city: "" }
    );
  };

  return (
    <div className="flex flex-col gap-2 w-full">
      {hasAddresses && (
        <Dropdown
          label="Address *"
          value={user.addressChoice || ""}
          options={[...addresses.map(addressLabel), NEW_ADDRESS_CHOICE]}
          onSelect={handleChoice}
          searchable={addresses.length > 8}
        />
      )}

      {isNewAddress && (
        <>
          <InputField
            label="Street address *"
            value={user.streetAddress || ""}
            onChange={(e) => onChange({ streetAddress: e.target.value })}
            placeholder="Storgatan 1"
            type="text"
          />
          <div className="flex gap-2 w-full">
            <div className="w-32 flex-none">
              <InputField
                label="Postal code *"
                value={user.postalCode || ""}
                onChange={(e) => onChange({ postalCode: e.target.value })}
                placeholder="123 45"
                type="text"
              />
            </div>
            <InputField
              label="City *"
              value={user.city || ""}
              onChange={(e) => onChange({ city: e.target.value })}
              placeholder="Stockholm"
              type="text"
            />
          </div>
        </>
      )}
    </div>
  );
};

export default AddressField;
